import CredentialRow from '@components/Employers/CredentialRow';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

interface Props {
  open: boolean;
  employerId: string;
  /** Shown in the confirmation copy so the admin can see whose credentials these are. */
  employerEmail: string;
  onClose: () => void;
}

/**
 * Reissue an employer's login password.
 *
 * Placed like SendContractDialog (own folder under Employers/, parent owns the open flag) but built
 * like EmployerCreateDialog: it calls the BFF over axios rather than GraphQL, because the Java
 * service does not hash passwords and the plaintext must never travel through the generic /graphql
 * proxy. See backend/src/routes/employers.ts.
 *
 * Two states only, pre-generate and post-generate. There is deliberately no "type your own
 * password" field: the account-creation flow does not have one either, and a hand-picked password
 * read aloud over a call is the thing this is meant to replace.
 *
 * The plaintext lives in component state and nowhere else. It is cleared on close, so reopening
 * the dialog cannot show a password that was issued minutes ago.
 */
const PasswordDialog: React.FC<Props> = ({
  open,
  employerId,
  employerEmail,
  onClose,
}) => {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on every open rather than on close: a close that unmounts mid-request would otherwise
  // leave the old value to reappear.
  useEffect(() => {
    if (open) {
      setPassword('');
      setSaving(false);
      setError(null);
    }
  }, [open]);

  const generate = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.patch(
        `/api/employers/${employerId}/password`,
        {},
      );
      setPassword(data.password);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not update the password',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      <DialogTitle>Update password</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {password ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert severity="warning">
              This password is shown once and is not stored anywhere. Copy it now.
            </Alert>
            <CredentialRow label="Email" value={employerEmail} />
            <CredentialRow label="Password" value={password} />
          </Box>
        ) : (
          <Typography variant="body2">
            Generates a new password for <strong>{employerEmail}</strong>. It is
            shown once here: copy it and share it with the employer directly, for
            example so a Premium Hiring client can sign in and review their own
            applicants. Their current password stops working immediately.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {password ? (
          <Button variant="contained" onClick={onClose}>
            I have copied the password
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button disabled={saving} variant="contained" onClick={generate}>
              {saving ? 'Generating…' : 'Generate new password'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PasswordDialog;
